import { useEffect, useState } from 'react'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Stack from '@mui/joy/Stack'
import { isAllowedPromoDeeplink, promoPageDeeplink } from '@numeo/types'
import { MOBILE_APP_SCHEME, mobileWebUrl } from '@numeo/types-core'
import { apiErrorMessage } from '../../../../api/mobileHomeLayout'
import { mobileFactoringPromptApi } from '../../../../api/mobileFactoringPrompt'

/** Where "No, I don't" on the mobile "Do you have a factoring?" prompt sends users, Inbox and thread alike. */
export function FactoringNoLinkCard({ notify }: { notify: (message: string, color: 'success' | 'danger') => void }) {
    const [value, setValue] = useState('')
    const [saved, setSaved] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        mobileFactoringPromptApi
            .get()
            .then((link) => {
                setValue(link)
                setSaved(link)
            })
            .catch((err) => notify(apiErrorMessage(err), 'danger'))
    }, [notify])

    const trimmed = value.trim()
    const valid = trimmed === '' || isAllowedPromoDeeplink(trimmed)

    async function save() {
        setSaving(true)
        try {
            const warning = await mobileFactoringPromptApi.save(trimmed)
            setSaved(trimmed)
            notify(warning ?? (trimmed ? 'Saved. "No" now opens this link.' : 'Saved. "No" only saves the answer.'), warning ? 'danger' : 'success')
        } catch (err) {
            notify(apiErrorMessage(err), 'danger')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Card variant="outlined" sx={{ mb: 3 }}>
            <FormControl error={!valid}>
                <FormLabel>Factoring prompt: link opened by &quot;No, I don&apos;t&quot;</FormLabel>
                <Stack direction="row" spacing={1}>
                    <Input
                        sx={{ flex: 1 }}
                        placeholder={promoPageDeeplink('basicblock')}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        disabled={saved === null}
                    />
                    <Button disabled={!valid || saved === null || trimmed === saved} loading={saving} onClick={() => void save()}>
                        Save
                    </Button>
                </Stack>
                <FormHelperText>
                    {valid
                        ? 'Empty = the app only saves the answer. Applies to every user, Inbox and email thread.'
                        : `Must be a ${MOBILE_APP_SCHEME} or ${mobileWebUrl('')} link the app knows.`}
                </FormHelperText>
            </FormControl>
        </Card>
    )
}
